// Country-pack tax-rule administration (platform). View / create future-dated
// versions / retire the effective-dated payment-tax rules that drive non-employee
// withholding across TZ/KE/RW. New rules are drafts until a qualified tax
// professional activates them — tenants never edit statutory rates.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Tag, Button, Modal, Form, Input, InputNumber, Select, Switch, message,
  Typography, Space, Alert, Tooltip,
} from 'antd';
import {
  listTaxRules, createTaxRule, retireTaxRule, activateTaxRule, type PaymentTaxRule,
} from '../../services/taxRulesService';

const { Title, Paragraph, Text } = Typography;

const TREATMENTS = ['WITHHOLDING_TAX', 'EMPLOYMENT_INCOME', 'EXEMPT'];
const RESIDENCIES = ['ANY', 'RESIDENT', 'NON_RESIDENT'];
const RELATIONSHIPS = ['ANY', 'EMPLOYEE', 'CONTRACTOR', 'CONSULTANT', 'AGENT', 'DIRECTOR', 'CASUAL_WORKER', 'INTERN', 'OTHER'];
const LEGAL_TYPES = ['ANY', 'INDIVIDUAL', 'SOLE_PROPRIETOR', 'COMPANY', 'PARTNERSHIP', 'GOVERNMENT_ENTITY', 'NON_PROFIT'];
const CLASSIFICATIONS = ['PROFESSIONAL_SERVICE_FEE', 'AGENT_COMMISSION', 'BROKERAGE_COMMISSION', 'MANAGEMENT_FEE', 'TECHNICAL_SERVICE_FEE', 'DIRECTOR_FEE', 'REFERRAL_FEE', 'EMPLOYMENT_COMMISSION', 'OTHER'];
const FINALITIES = ['NA', 'FINAL', 'NON_FINAL'];
const COUNTRIES = ['TZ', 'KE', 'RW'];

const STATUS_COLOR: Record<string, string> = { active: 'green', draft: 'gold', retired: 'default' };

export function PaymentTaxRulesPage() {
  const [rules, setRules] = useState<PaymentTaxRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setRules(await listTaxRules(country));
    } catch {
      message.error('Could not load tax rules');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      await createTaxRule({
        ...v,
        rate: String(v.rate ?? 0),
        threshold: String(v.threshold ?? 0),
      });
      message.success('Rule created as a draft — activate it after tax sign-off');
      setModalOpen(false);
      form.resetFields();
      await load();
    } catch (e: any) {
      if (e?.errorFields) return; // validation
      message.error(e?.response?.data?.message || 'Could not create rule');
    }
  };

  const doActivate = async (r: PaymentTaxRule) => {
    Modal.confirm({
      title: `Activate ${r.code}?`,
      content: 'Confirm a qualified tax professional has verified this rate. It will start applying to matching payments.',
      okText: 'Activate',
      onOk: async () => { try { await activateTaxRule(r.id); message.success('Activated'); await load(); } catch { message.error('Failed'); } },
    });
  };
  const doRetire = async (r: PaymentTaxRule) => {
    Modal.confirm({
      title: `Retire ${r.code}?`,
      content: 'It will stop applying to new payments. History is preserved.',
      okText: 'Retire', okButtonProps: { danger: true },
      onOk: async () => { try { await retireTaxRule(r.id); message.success('Retired'); await load(); } catch { message.error('Failed'); } },
    });
  };

  const columns = useMemo(() => [
    { title: 'Country', dataIndex: 'country_code', width: 80 },
    { title: 'Code', dataIndex: 'code', render: (c: string) => <Text code>{c}</Text> },
    { title: 'Classification', dataIndex: 'payment_classification', render: (v: string) => v.replace(/_/g, ' ').toLowerCase() },
    { title: 'Who', render: (_: any, r: PaymentTaxRule) => <Text type="secondary">{[r.worker_relationship, r.residency, r.recipient_legal_type].filter((x) => x !== 'ANY').join(' · ') || 'any'}</Text> },
    { title: 'Treatment', dataIndex: 'treatment', render: (t: string) => t === 'WITHHOLDING_TAX' ? 'WHT' : t.replace(/_/g, ' ').toLowerCase() },
    { title: 'Rate', dataIndex: 'rate', align: 'right' as const, render: (r: string, row: PaymentTaxRule) => row.treatment === 'WITHHOLDING_TAX' ? `${(Number(r) * 100).toFixed(2)}%` : '—' },
    { title: 'Finality', dataIndex: 'finality', render: (f: string) => f === 'NA' ? '—' : f.toLowerCase() },
    { title: 'Effective', dataIndex: 'effective_from', width: 110 },
    {
      title: 'Status', dataIndex: 'status', render: (s: string, r: PaymentTaxRule) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[s]}>{s}</Tag>
          {r.review_required && s !== 'retired' && <Tooltip title="Awaiting tax-professional sign-off"><Tag color="orange">review</Tag></Tooltip>}
        </Space>
      ),
    },
    {
      title: 'Actions', render: (_: any, r: PaymentTaxRule) => (
        <Space>
          {r.status === 'draft' && <Button size="small" type="link" onClick={() => doActivate(r)}>Activate</Button>}
          {r.status !== 'retired' && <Button size="small" type="link" danger onClick={() => doRetire(r)}>Retire</Button>}
        </Space>
      ),
    },
  ], []);

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Country-pack tax rules</Title>
      <Paragraph type="secondary">
        Effective-dated withholding rules for non-employee payments (consultants, contractors, agents), per country pack.
        New rules start as <Tag color="gold">draft</Tag>; activate only after a qualified local tax professional confirms the rate.
      </Paragraph>
      <Alert
        type="warning" showIcon style={{ marginBottom: 16 }}
        message="Statutory rates are platform-controlled"
        description="Tenants can select which payment categories apply but cannot change these rates. All seeded rates are placeholders until locally confirmed."
      />
      <Space style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="All countries" style={{ width: 160 }} value={country} onChange={setCountry}
          options={COUNTRIES.map((c) => ({ value: c, label: c }))} />
        <Button type="primary" onClick={() => setModalOpen(true)}>New rule / version</Button>
      </Space>

      <Table rowKey="id" loading={loading} dataSource={rules} columns={columns as any} size="small" pagination={{ pageSize: 20 }} />

      <Modal title="New tax rule" open={modalOpen} onOk={submit} onCancel={() => setModalOpen(false)} okText="Create draft" width={640}>
        <Form form={form} layout="vertical" initialValues={{ country_code: 'TZ', treatment: 'WITHHOLDING_TAX', residency: 'ANY', worker_relationship: 'ANY', recipient_legal_type: 'ANY', finality: 'NON_FINAL', rate: 0.05, treaty_override_allowed: false }}>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="code" label="Rule code" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Input placeholder="TZ_RESIDENT_PROFESSIONAL_SERVICE" />
            </Form.Item>
            <Form.Item name="country_code" label="Country" rules={[{ required: true }]}>
              <Select options={COUNTRIES.map((c) => ({ value: c, label: c }))} style={{ width: 120 }} />
            </Form.Item>
          </Space>
          <Form.Item name="payment_classification" label="Payment classification" rules={[{ required: true }]}>
            <Select options={CLASSIFICATIONS.map((c) => ({ value: c, label: c }))} />
          </Form.Item>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="worker_relationship" label="Worker relationship" style={{ flex: 1 }}>
              <Select options={RELATIONSHIPS.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
            <Form.Item name="residency" label="Residency" style={{ flex: 1 }}>
              <Select options={RESIDENCIES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
            <Form.Item name="recipient_legal_type" label="Recipient type" style={{ flex: 1 }}>
              <Select options={LEGAL_TYPES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
          </Space>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="treatment" label="Treatment" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select options={TREATMENTS.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
            <Form.Item name="rate" label="Rate (0.15 = 15%)" style={{ flex: 1 }}>
              <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="finality" label="Finality" style={{ flex: 1 }}>
              <Select options={FINALITIES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
          </Space>
          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item name="tax_code" label="Tax / filing code" style={{ flex: 1 }}>
              <Input placeholder="WHT_SERVICE_RESIDENT" />
            </Form.Item>
            <Form.Item name="treaty_override_allowed" label="Treaty override" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} placeholder="Source / confirmation reference" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PaymentTaxRulesPage;
